export const BACKGROUND_OPTIONS = [
  {
    id: 'white',
    label: 'White',
    description: 'Bright and airy surfaces',
  },
  {
    id: 'black',
    label: 'Black',
    description: 'High-contrast neutral depth',
  },
  {
    id: 'navy',
    label: 'Navy',
    description: 'Blue-toned editorial depth',
  },
];

export const ACCENT_GROUPS = [
  {
    id: 'pastel',
    label: 'Pastel',
    description: 'Soft, calm highlights',
    palette: {
      primary: { main: '#7C8CF8', light: '#A5B4FC', dark: '#5B6CE1', contrastText: '#FFFFFF' },
      secondary: { main: '#F472B6', light: '#F9A8D4', dark: '#DB2777', contrastText: '#FFFFFF' },
      success: { main: '#34D399', light: '#6EE7B7', dark: '#10B981' },
      warning: { main: '#FBBF24', light: '#FCD34D', dark: '#D97706' },
      error: { main: '#FB7185', light: '#FDA4AF', dark: '#E11D48' },
      info: { main: '#38BDF8', light: '#7DD3FC', dark: '#0EA5E9' },
    },
  },
  {
    id: 'grounded',
    label: 'Grounded',
    description: 'Earthy, steady accents',
    palette: {
      primary: { main: '#7C5C3B', light: '#A78B6D', dark: '#5C4228', contrastText: '#FFFFFF' },
      secondary: { main: '#5E7A5D', light: '#7F9B7E', dark: '#435743', contrastText: '#FFFFFF' },
      success: { main: '#3F8F6B', light: '#6DB28F', dark: '#2D6A4F' },
      warning: { main: '#C68642', light: '#D8A15D', dark: '#9A6324' },
      error: { main: '#B65A4C', light: '#D38A7F', dark: '#8F4338' },
      info: { main: '#517A8A', light: '#7B9EAB', dark: '#36535F' },
    },
  },
  {
    id: 'ocean',
    label: 'Ocean',
    description: 'Clean blues and teals',
    palette: {
      primary: { main: '#0F766E', light: '#14B8A6', dark: '#115E59', contrastText: '#FFFFFF' },
      secondary: { main: '#2563EB', light: '#60A5FA', dark: '#1D4ED8', contrastText: '#FFFFFF' },
      success: { main: '#059669', light: '#34D399', dark: '#047857' },
      warning: { main: '#F59E0B', light: '#FBBF24', dark: '#D97706' },
      error: { main: '#DC2626', light: '#F87171', dark: '#B91C1C' },
      info: { main: '#0284C7', light: '#38BDF8', dark: '#0369A1' },
    },
  },
  {
    id: 'ember',
    label: 'Ember',
    description: 'Warm reds and amber tones',
    palette: {
      primary: { main: '#D9485F', light: '#FB7185', dark: '#BE123C', contrastText: '#FFFFFF' },
      secondary: { main: '#F59E0B', light: '#FBBF24', dark: '#D97706', contrastText: '#1F2937' },
      success: { main: '#16A34A', light: '#4ADE80', dark: '#15803D' },
      warning: { main: '#EA580C', light: '#FB923C', dark: '#C2410C' },
      error: { main: '#DC2626', light: '#F87171', dark: '#B91C1C' },
      info: { main: '#7C3AED', light: '#A78BFA', dark: '#6D28D9' },
    },
  },
];

const BACKGROUND_TOKENS = {
  white: {
    light: {
      default: '#F8FAFC',
      paper: '#FFFFFF',
      text: { primary: '#0F172A', secondary: '#475569' },
      divider: '#E2E8F0',
    },
    dark: {
      default: '#16181D',
      paper: '#20242B',
      text: { primary: '#F8FAFC', secondary: '#CBD5E1' },
      divider: 'rgba(255,255,255,0.12)',
    },
  },
  black: {
    light: {
      default: '#F3F4F6',
      paper: '#FFFFFF',
      text: { primary: '#111827', secondary: '#4B5563' },
      divider: '#D1D5DB',
    },
    dark: {
      default: '#050505',
      paper: '#111111',
      text: { primary: '#FAFAFA', secondary: '#B4B4B4' },
      divider: 'rgba(255,255,255,0.10)',
    },
  },
  navy: {
    light: {
      default: '#F3F7FF',
      paper: '#FFFFFF',
      text: { primary: '#102A43', secondary: '#486581' },
      divider: '#D7E3F4',
    },
    dark: {
      default: '#0F172A',
      paper: '#162033',
      text: { primary: '#E2E8F0', secondary: '#94A3B8' },
      divider: 'rgba(148,163,184,0.22)',
    },
  },
};

const ACCENT_GROUP_MAP = ACCENT_GROUPS.reduce((acc, group) => {
  acc[group.id] = group;
  return acc;
}, {});

export const DEFAULT_THEME_PREFERENCES = {
  light: {
    background: 'white',
    accentGroup: 'ocean',
  },
  dark: {
    background: 'navy',
    accentGroup: 'pastel',
  },
};

const normalizeModePreferences = (modePreferences, fallback) => {
  const background = BACKGROUND_TOKENS[modePreferences?.background] ? modePreferences.background : fallback.background;
  const accentGroup = ACCENT_GROUP_MAP[modePreferences?.accentGroup] ? modePreferences.accentGroup : fallback.accentGroup;

  return {
    background,
    accentGroup,
  };
};

export const normalizeThemePreferences = (preferences) => ({
  light: normalizeModePreferences(preferences?.light, DEFAULT_THEME_PREFERENCES.light),
  dark: normalizeModePreferences(preferences?.dark, DEFAULT_THEME_PREFERENCES.dark),
});

export const getThemeSelection = (mode, preferences) => {
  const normalized = normalizeThemePreferences(preferences);
  return normalized[mode] || normalized.light;
};

export const getThemeTokens = (mode, preferences) => {
  const selection = getThemeSelection(mode, preferences);
  const background = BACKGROUND_TOKENS[selection.background][mode];
  const accents = ACCENT_GROUP_MAP[selection.accentGroup].palette;

  return {
    selection,
    background,
    accents,
  };
};